import { TaskStatus, TaskStatusDefinition } from '../types/Task';

const normalizeStatusCode = (value: string): string => value.trim().toLowerCase();

export type TaskStatusWorkflowWarning = 'isolated' | 'deadEnd' | 'unreachable';

const fallbackTaskStatusLabel = (code: string): string => {
  if (!code) return '';
  const withSpaces = code
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[-_]+/g, ' ')
    .trim();

  return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
};

export const sortTaskStatuses = (statuses: TaskStatusDefinition[]): TaskStatusDefinition[] => (
  [...statuses].sort((left, right) => {
    const orderDiff = left.displayOrder - right.displayOrder;
    if (orderDiff !== 0) return orderDiff;
    return left.name.localeCompare(right.name);
  })
);

export const getTaskStatusDefinition = (
  code: TaskStatus,
  statuses: TaskStatusDefinition[],
): TaskStatusDefinition | undefined => {
  const normalizedCode = normalizeStatusCode(code);
  return statuses.find(status => normalizeStatusCode(status.code) === normalizedCode);
};

export const getTaskStatusLabel = (code: TaskStatus, statuses: TaskStatusDefinition[]): string => {
  return getTaskStatusDefinition(code, statuses)?.name ?? fallbackTaskStatusLabel(code);
};

export const isTaskStatusCompleted = (code: TaskStatus, statuses: TaskStatusDefinition[]): boolean => {
  return Boolean(getTaskStatusDefinition(code, statuses)?.isCompleted);
};

export const getBlockedTaskStatusesByAssignee = (
  currentStatus: TaskStatus,
  statuses: TaskStatusDefinition[],
  assignedToUserId?: string,
): TaskStatus[] => {
  if (assignedToUserId) {
    return [];
  }

  const currentDefinition = getTaskStatusDefinition(currentStatus, statuses);
  if (!currentDefinition) {
    return [];
  }

  return currentDefinition.transitionToCodes.filter(nextCode => {
    const definition = getTaskStatusDefinition(nextCode, statuses);
    return Boolean(definition?.requiresAssignee);
  });
};

export const getAllowedTaskStatuses = (
  currentStatus: TaskStatus,
  statuses: TaskStatusDefinition[],
  assignedToUserId?: string,
): TaskStatus[] => {
  const currentDefinition = getTaskStatusDefinition(currentStatus, statuses);
  const normalizedCurrentStatus = normalizeStatusCode(currentStatus);

  if (!currentDefinition) {
    return normalizedCurrentStatus ? [normalizedCurrentStatus] : [];
  }

  const nextStatuses = currentDefinition.transitionToCodes
    .map(nextCode => normalizeStatusCode(nextCode))
    .filter(nextCode => {
      const definition = getTaskStatusDefinition(nextCode, statuses);
      return Boolean(definition) && (!definition!.requiresAssignee || Boolean(assignedToUserId));
    });

  return Array.from(new Set([currentDefinition.code, ...nextStatuses]));
};

export const getIncomingTaskStatuses = (
  targetStatus: TaskStatus,
  statuses: TaskStatusDefinition[],
): TaskStatus[] => {
  const normalizedTargetStatus = normalizeStatusCode(targetStatus);
  if (!normalizedTargetStatus) {
    return [];
  }

  return statuses
    .filter(status => status.transitionToCodes.some(nextCode => normalizeStatusCode(nextCode) === normalizedTargetStatus))
    .map(status => status.code);
};

export const getReachableTaskStatuses = (statuses: TaskStatusDefinition[]): TaskStatus[] => {
  const statusByCode = new Map<string, TaskStatusDefinition>();
  statuses.forEach(status => {
    const normalizedCode = normalizeStatusCode(status.code);
    if (normalizedCode) {
      statusByCode.set(normalizedCode, status);
    }
  });

  const startCodes = statuses
    .filter(status => status.isDefault)
    .map(status => normalizeStatusCode(status.code))
    .filter(Boolean);

  const visited = new Set<string>();
  const stack = [...startCodes];

  while (stack.length > 0) {
    const currentCode = stack.pop();
    if (!currentCode || visited.has(currentCode)) {
      continue;
    }

    visited.add(currentCode);

    const status = statusByCode.get(currentCode);
    if (!status) {
      continue;
    }

    status.transitionToCodes.forEach(nextCode => {
      const normalizedNextCode = normalizeStatusCode(nextCode);
      if (normalizedNextCode && statusByCode.has(normalizedNextCode) && !visited.has(normalizedNextCode)) {
        stack.push(normalizedNextCode);
      }
    });
  }

  return Array.from(visited);
};

export const getTaskStatusWorkflowWarnings = (
  targetStatus: TaskStatus,
  statuses: TaskStatusDefinition[],
): TaskStatusWorkflowWarning[] => {
  const status = getTaskStatusDefinition(targetStatus, statuses);
  if (!status) {
    return [];
  }

  const normalizedCode = normalizeStatusCode(status.code);
  if (!normalizedCode) {
    return [];
  }

  const incomingStatuses = getIncomingTaskStatuses(status.code, statuses)
    .filter(code => normalizeStatusCode(code) !== normalizedCode);
  const outgoingStatuses = status.transitionToCodes
    .map(code => normalizeStatusCode(code))
    .filter(code => Boolean(code) && code !== normalizedCode && Boolean(getTaskStatusDefinition(code, statuses)));
  const reachableStatuses = new Set(getReachableTaskStatuses(statuses).map(code => normalizeStatusCode(code)));
  const warnings: TaskStatusWorkflowWarning[] = [];

  if (incomingStatuses.length === 0 && outgoingStatuses.length === 0) {
    warnings.push('isolated');
  }

  if (!status.isCompleted && outgoingStatuses.length === 0) {
    warnings.push('deadEnd');
  }

  const hasDefaultStatuses = statuses.some(item => item.isDefault && normalizeStatusCode(item.code));
  if (hasDefaultStatuses && !status.isDefault && !reachableStatuses.has(normalizedCode)) {
    warnings.push('unreachable');
  }

  return warnings;
};

export const isTaskStatusBlockedByAssignee = (
  currentStatus: TaskStatus,
  nextStatus: TaskStatus,
  statuses: TaskStatusDefinition[],
  assignedToUserId?: string,
): boolean => getBlockedTaskStatusesByAssignee(currentStatus, statuses, assignedToUserId)
  .some(code => normalizeStatusCode(code) === normalizeStatusCode(nextStatus));

export const canTransitionTaskStatus = (
  currentStatus: TaskStatus,
  nextStatus: TaskStatus,
  statuses: TaskStatusDefinition[],
  assignedToUserId?: string,
): boolean => getAllowedTaskStatuses(currentStatus, statuses, assignedToUserId)
  .some(code => normalizeStatusCode(code) === normalizeStatusCode(nextStatus));